'use client'

import { ChevronRight } from '@mui/icons-material'
import { Box, Collapse, useMediaQuery } from '@mui/material'
import { type ReactNode, useId, useState } from 'react'

type DisclosureProps = {
  /** The header's content: a plain-text label, usually a `StepLabel`. */
  label: ReactNode
  /** Whether the content is open until the reader toggles it; the reader's choice then wins. */
  defaultExpanded?: boolean
  /**
   * Hides the header and keeps the content open. The content stays in the same place in the tree whether or not the
   * header shows, so its state survives the header appearing.
   */
  headerless?: boolean
  children: ReactNode
}

/** Height transition of the content region, in milliseconds. */
const COLLAPSE_MS = 120

/**
 * A plain-text header that folds its content away. The header is a full-width button with no chrome around it; its
 * chevron follows the label and shows on hover, on keyboard focus and while the content is open. The content stays
 * mounted while collapsed so nested disclosures keep their state.
 */
export default function Disclosure({ label, defaultExpanded = false, headerless = false, children }: DisclosureProps) {
  const contentId = useId()
  const [readerExpanded, setReaderExpanded] = useState<boolean | null>(null)
  const expanded = headerless || (readerExpanded ?? defaultExpanded)
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  return (
    <Box>
      {!headerless && (
        <Box
          component="button"
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setReaderExpanded(!expanded)}
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 0.25,
            m: 0,
            p: 0,
            py: 0.25,
            border: 0,
            bgcolor: 'transparent',
            color: 'text.secondary',
            font: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            '&:hover': { color: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            '&:hover .disclosure-chevron, &:focus-visible .disclosure-chevron': { opacity: 1 }
          }}
        >
          {label}
          <ChevronRight
            className="disclosure-chevron"
            fontSize="small"
            aria-hidden
            sx={{
              opacity: expanded ? 1 : 0,
              transform: expanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 120ms, opacity 120ms'
            }}
          />
        </Box>
      )}
      <Collapse id={contentId} in={expanded} timeout={reduceMotion ? 0 : COLLAPSE_MS}>
        <Box sx={headerless ? undefined : { pl: 2, pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  )
}
